import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Pencil, Trash2, Database, Download, Copy, MoreHorizontal, AlertTriangle, FileDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { FieldBuilder, SchemaPreview, ExampleGenerator, VersionHistory, VersionCompare, PublishVersionDialog, VersionRecord } from './standards';
import { 
  generateSchema, 
  generateExample, 
  generateFieldsArray,
  type FieldMapping, 
  type FieldLibraryItem 
} from '@/lib/admin/field-utils';
import { 
  downloadFile, 
  exportFullPackage, 
  exportJsonSchema, 
  exportTypeScript, 
  exportCsvFieldReference, 
  exportOpenApiComponent 
} from '@/lib/export';
import { DataStandard, SchemaField } from '@/lib/data-standards/schemas';
import { Json } from '@/integrations/supabase/types';

interface DataStandardRecord {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  version: string;
  category: string;
  category_name: string;
  display_order: number;
  is_active: boolean;
  maturity_level: 'draft' | 'trial' | 'normative' | 'deprecated';
  deprecation_notice: string | null;
  created_at: string;
  updated_at: string;
}

interface CategoryOption {
  value: string;
  label: string;
}

export function DataStandardsManager() {
  const { toast } = useToast();
  const [standards, setStandards] = useState<DataStandardRecord[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [lookupGroups, setLookupGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingStandard, setEditingStandard] = useState<DataStandardRecord | null>(null);
  const [customCategoryInput, setCustomCategoryInput] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    description: '',
    version: '1.0.0',
    category: '',
    category_name: '',
    display_order: 0,
    is_active: true,
    maturity_level: 'draft' as 'draft' | 'trial' | 'normative' | 'deprecated',
    deprecation_notice: '',
  });

  // Field mappings state (for visual builder)
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [customExample, setCustomExample] = useState('');
  const [customSchema, setCustomSchema] = useState('');

  const [activeEditorTab, setActiveEditorTab] = useState('basic');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [standardToDelete, setStandardToDelete] = useState<DataStandardRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    
    // Fetch categories, standards, and lookup groups in parallel
    const [categoriesRes, standardsRes, lookupsRes] = await Promise.all([
      supabase.from('categories').select('slug, name').order('display_order'),
      supabase.from('data_standards').select('*').order('category, display_order'),
      supabase.from('lookups').select('lookup_group').order('lookup_group'),
    ]);

    if (categoriesRes.data) {
      setCategories(categoriesRes.data.map(c => ({ value: c.slug, label: c.name })));
    }
    
    if (standardsRes.error) {
      toast({ title: 'Error fetching standards', description: standardsRes.error.message, variant: 'destructive' });
    } else if (standardsRes.data) {
      setStandards(standardsRes.data as DataStandardRecord[]);
    }

    if (lookupsRes.data) {
      const uniqueGroups = [...new Set(lookupsRes.data.map(l => l.lookup_group))];
      setLookupGroups(uniqueGroups);
    }
    
    setLoading(false);
  };

  const resetForm = () => {
    const defaultCategory = categories[0];
    setFormData({
      slug: '',
      title: '',
      description: '',
      version: '1.0.0',
      category: defaultCategory?.value || '',
      category_name: defaultCategory?.label || '',
      display_order: 0,
      is_active: true,
      maturity_level: 'draft',
      deprecation_notice: '',
    });
    setFieldMappings([]);
    setCustomExample('');
    setCustomSchema('');
    setEditingStandard(null);
    setShowCustomCategory(false);
    setCustomCategoryInput('');
  };

  const openCreateEditor = () => {
    resetForm();
    setActiveEditorTab('basic');
    setEditorOpen(true);
  };

  const openEditEditor = async (standard: DataStandardRecord) => {
    setEditingStandard(standard);
    setFormData({
      slug: standard.slug,
      title: standard.title,
      description: standard.description || '',
      version: standard.version,
      category: standard.category,
      category_name: standard.category_name,
      display_order: standard.display_order,
      is_active: standard.is_active,
      maturity_level: standard.maturity_level || 'draft',
      deprecation_notice: standard.deprecation_notice || '',
    });

    // Fetch field mappings for this standard
    const { data: mappings } = await supabase
      .from('standard_field_mappings')
      .select(`
        id,
        field_id,
        is_required,
        default_value,
        lookup_group,
        inline_enum,
        display_order,
        notes,
        field_library (
          id,
          slug,
          name,
          description,
          data_type,
          format,
          pattern,
          min_length,
          max_length,
          minimum,
          maximum,
          is_common,
          display_order
        )
      `)
      .eq('standard_id', standard.id)
      .order('display_order');

    if (mappings) {
      const fieldMappingsData: FieldMapping[] = mappings.map((m) => ({
        id: m.id,
        field_id: m.field_id,
        field: m.field_library as unknown as FieldLibraryItem,
        is_required: m.is_required,
        default_value: m.default_value,
        lookup_group: m.lookup_group,
        inline_enum: m.inline_enum as string[] | null,
        display_order: m.display_order || 0,
        notes: m.notes,
      }));
      setFieldMappings(fieldMappingsData);
    } else {
      setFieldMappings([]);
    }

    setCustomExample('');
    setCustomSchema('');
    setActiveEditorTab('basic');
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.slug) {
      toast({ title: 'Title and slug are required', variant: 'destructive' });
      return;
    }

    setIsSaving(true);

    // Only save metadata - schema, example, and fields are generated dynamically from field mappings
    const payload = {
      slug: formData.slug,
      title: formData.title,
      description: formData.description || null,
      version: formData.version,
      category: formData.category,
      category_name: categories.find(c => c.value === formData.category)?.label || formData.category_name,
      display_order: formData.display_order,
      is_active: formData.is_active,
      maturity_level: formData.maturity_level,
      deprecation_notice: formData.deprecation_notice || null,
    };

    let standardId = editingStandard?.id;

    if (editingStandard) {
      const { error } = await supabase
        .from('data_standards')
        .update(payload)
        .eq('id', editingStandard.id);

      if (error) {
        toast({ title: 'Error updating standard', description: error.message, variant: 'destructive' });
        setIsSaving(false);
        return;
      }
    } else {
      const { data, error } = await supabase
        .from('data_standards')
        .insert([payload])
        .select('id')
        .single();

      if (error) {
        toast({ title: 'Error creating standard', description: error.message, variant: 'destructive' });
        setIsSaving(false);
        return;
      }
      standardId = data.id;
    }

    // Sync field mappings - this is the source of truth for schema/example generation
    if (standardId) {
      await syncFieldMappings(standardId, fieldMappings);
    }

    toast({ title: editingStandard ? 'Standard updated' : 'Standard created' });
    setEditorOpen(false);
    fetchData();
    setIsSaving(false);
  };

  const syncFieldMappings = async (standardId: string, mappings: FieldMapping[]) => {
    // Get existing mappings
    const { data: existing } = await supabase
      .from('standard_field_mappings')
      .select('id, field_id')
      .eq('standard_id', standardId);

    const existingIds = new Set(existing?.map(e => e.id) || []);
    const currentMappingIds = new Set(mappings.filter(m => m.id).map(m => m.id));
    
    // Delete removed mappings
    const toDelete = existing?.filter(e => !currentMappingIds.has(e.id)) || [];
    if (toDelete.length > 0) {
      await supabase
        .from('standard_field_mappings')
        .delete()
        .in('id', toDelete.map(d => d.id));
    }

    // Upsert current mappings
    for (const mapping of mappings) {
      const mappingPayload = {
        standard_id: standardId,
        field_id: mapping.field_id,
        is_required: mapping.is_required,
        default_value: mapping.default_value,
        lookup_group: mapping.lookup_group,
        inline_enum: mapping.inline_enum,
        display_order: mapping.display_order,
        notes: mapping.notes,
      };

      if (mapping.id && existingIds.has(mapping.id)) {
        await supabase
          .from('standard_field_mappings')
          .update(mappingPayload)
          .eq('id', mapping.id);
      } else {
        await supabase
          .from('standard_field_mappings')
          .insert(mappingPayload);
      }
    }
  };

  const confirmDelete = (standard: DataStandardRecord) => {
    setStandardToDelete(standard);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!standardToDelete) return;
    
    setIsDeleting(true);

    // Delete field mappings first
    await supabase
      .from('standard_field_mappings')
      .delete()
      .eq('standard_id', standardToDelete.id);

    const { error } = await supabase.from('data_standards').delete().eq('id', standardToDelete.id);
    
    if (error) {
      toast({ title: 'Error deleting standard', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Standard deleted' });
      fetchData();
    }
    
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setStandardToDelete(null);
  };

  const duplicateStandard = (standard: DataStandardRecord) => {
    setEditingStandard(null);
    setFormData({
      slug: standard.slug + '-copy',
      title: standard.title + ' (Copy)',
      description: standard.description || '',
      version: standard.version,
      category: standard.category,
      category_name: standard.category_name,
      display_order: standard.display_order + 1,
      is_active: false,
      maturity_level: 'draft',
      deprecation_notice: '',
    });
    setFieldMappings([]);
    setCustomExample('');
    setCustomSchema('');
    setActiveEditorTab('basic');
    setEditorOpen(true);
  };

  const handlePublishVersion = async (newVersion: string, changelog: string) => {
    if (!editingStandard) return;

    // Generate schema, example, and fields for version snapshot
    const currentSchema = generateSchema(fieldMappings);
    const currentExample = generateExample(fieldMappings);
    const currentFields = generateFieldsArray(fieldMappings);

    // Mark all existing versions as not current
    await supabase
      .from('data_standard_versions')
      .update({ is_current: false })
      .eq('standard_id', editingStandard.id);

    // Insert new version - this snapshots the current state
    const { error: versionError } = await supabase.from('data_standard_versions').insert({
      standard_id: editingStandard.id,
      version: newVersion,
      schema: currentSchema as unknown as Json,
      example: currentExample as unknown as Json,
      fields: currentFields as unknown as Json,
      changelog,
      is_current: true,
    });

    if (versionError) {
      toast({ title: 'Error publishing version', description: versionError.message, variant: 'destructive' });
      return;
    }

    // Update data_standards with new version number
    await supabase
      .from('data_standards')
      .update({ version: newVersion })
      .eq('id', editingStandard.id);

    setFormData({ ...formData, version: newVersion });
    setEditingStandard({ ...editingStandard, version: newVersion });
    toast({ title: `Version v${newVersion} published successfully` });
  };

  const handleRollback = (version: VersionRecord) => {
    // Copy version data to form state
    setFieldMappings([]); // Clear field mappings, user will need to reload
    setCustomSchema(JSON.stringify(version.schema, null, 2));
    setCustomExample(JSON.stringify(version.example, null, 2));
    setActiveEditorTab('advanced');
  };

  const exportStandard = async (standard: DataStandardRecord) => {
    // Fetch field mappings to generate schema/example for export
    const { data: mappings } = await supabase
      .from('standard_field_mappings')
      .select(`
        id, field_id, is_required, default_value, lookup_group, inline_enum, display_order, notes,
        field_library (id, slug, name, description, data_type, format, pattern, min_length, max_length, minimum, maximum, is_common, display_order)
      `)
      .eq('standard_id', standard.id)
      .order('display_order');

    const fieldMappingsData: FieldMapping[] = (mappings || []).map((m) => ({
      id: m.id,
      field_id: m.field_id,
      field: m.field_library as unknown as FieldLibraryItem,
      is_required: m.is_required,
      default_value: m.default_value,
      lookup_group: m.lookup_group,
      inline_enum: m.inline_enum as string[] | null,
      display_order: m.display_order || 0,
      notes: m.notes,
    }));

    const exportData = {
      slug: standard.slug,
      title: standard.title,
      description: standard.description,
      version: standard.version,
      category: standard.category,
      schema: generateSchema(fieldMappingsData),
      example: generateExample(fieldMappingsData),
      fields: generateFieldsArray(fieldMappingsData),
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${standard.slug}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Convert DataStandardRecord to DataStandard format for export utilities
  // This requires fetching field mappings to generate schema/example dynamically
  const toDataStandardWithMappings = async (record: DataStandardRecord): Promise<DataStandard> => {
    const { data: mappings } = await supabase
      .from('standard_field_mappings')
      .select(`
        id, field_id, is_required, default_value, lookup_group, inline_enum, display_order, notes,
        field_library (id, slug, name, description, data_type, format, pattern, min_length, max_length, minimum, maximum, is_common, display_order)
      `)
      .eq('standard_id', record.id)
      .order('display_order');

    const fieldMappingsData: FieldMapping[] = (mappings || []).map((m) => ({
      id: m.id,
      field_id: m.field_id,
      field: m.field_library as unknown as FieldLibraryItem,
      is_required: m.is_required,
      default_value: m.default_value,
      lookup_group: m.lookup_group,
      inline_enum: m.inline_enum as string[] | null,
      display_order: m.display_order || 0,
      notes: m.notes,
    }));

    const schemaFields: SchemaField[] = fieldMappingsData.map((m) => ({
      name: m.field.slug,
      type: m.field.data_type,
      description: m.field.description || '',
      required: m.is_required,
      format: m.field.format || undefined,
    }));

    return {
      id: record.slug,
      title: record.title,
      description: record.description || '',
      version: record.version,
      category: record.category,
      lastUpdated: new Date(record.updated_at).toLocaleDateString(),
      schema: generateSchema(fieldMappingsData),
      example: generateExample(fieldMappingsData),
      fields: schemaFields,
    };
  };

  // Bulk export functions
  type BulkExportFormat = 'full-package' | 'json-schema' | 'typescript' | 'csv' | 'openapi';

  const handleBulkExport = async (standardsToExport: DataStandardRecord[], format: BulkExportFormat) => {
    toast({ title: `Exporting ${standardsToExport.length} standard(s)...` });
    
    const exportedData = await Promise.all(standardsToExport.map(async (record) => {
      const standard = await toDataStandardWithMappings(record);
      
      switch (format) {
        case 'full-package':
          return {
            filename: `${record.slug}.json`,
            content: exportFullPackage(standard),
          };
        case 'json-schema':
          return {
            filename: `${record.slug}.schema.json`,
            content: exportJsonSchema(standard.schema, standard.title),
          };
        case 'typescript':
          return {
            filename: `${record.slug}.d.ts`,
            content: exportTypeScript(
              standard.schema,
              standard.fields,
              standard.title,
              standard.version
            ),
          };
        case 'csv':
          return {
            filename: `${record.slug}.csv`,
            content: exportCsvFieldReference(standard.fields, standard.schema),
          };
        case 'openapi':
          return {
            filename: `${record.slug}.openapi.json`,
            content: exportOpenApiComponent(
              standard.schema,
              standard.title,
              standard.version,
              standard.description
            ),
          };
      }
    }));

    // Download as individual files or zip
    if (exportedData.length === 1) {
      const { filename, content } = exportedData[0];
      const mimeType = format === 'typescript' ? 'text/typescript' : format === 'csv' ? 'text/csv' : 'application/json';
      downloadFile(content, filename, mimeType);
    } else {
      // For multiple files, create a combined JSON export
      const combined = {
        meta: {
          generator: 'AutomotiveAPI Admin',
          exportedAt: new Date().toISOString(),
          format,
          count: exportedData.length,
        },
        standards: exportedData.reduce((acc, { filename, content }) => {
          acc[filename] = format === 'csv' || format === 'typescript' ? content : JSON.parse(content);
          return acc;
        }, {} as Record<string, unknown>),
      };
      downloadFile(JSON.stringify(combined, null, 2), `data-standards-${format}.json`, 'application/json');
    }

    toast({ title: `Exported ${exportedData.length} standard(s) as ${format}` });
  };

  const handleExportAll = (format: BulkExportFormat) => {
    handleBulkExport(standards, format);
  };

  const handleExportCategory = (categoryId: string, format: BulkExportFormat) => {
    const categoryStandards = standards.filter(s => s.category === categoryId);
    handleBulkExport(categoryStandards, format);
  };

  // Group standards by category
  const groupedStandards = standards.reduce((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {} as Record<string, DataStandardRecord[]>);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Data Standards"
        count={standards.length}
        onAdd={openCreateEditor}
        addLabel="Add Standard"
        actions={
          standards.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <FileDown className="h-4 w-4 mr-2" />
                  Bulk Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Download className="h-4 w-4 mr-2" />
                    Export All ({standards.length})
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => handleExportAll('full-package')}>
                      Full JSON Package
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('json-schema')}>
                      JSON Schema
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('typescript')}>
                      TypeScript Definitions
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('csv')}>
                      CSV Field Reference
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('openapi')}>
                      OpenAPI Components
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                {Object.entries(groupedStandards).map(([categoryId, categoryStandards]) => (
                  <DropdownMenuSub key={categoryId}>
                    <DropdownMenuSubTrigger>
                      <Download className="h-4 w-4 mr-2" />
                      {categoryStandards[0]?.category_name || categoryId} ({categoryStandards.length})
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => handleExportCategory(categoryId, 'full-package')}>
                        Full JSON Package
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportCategory(categoryId, 'json-schema')}>
                        JSON Schema
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportCategory(categoryId, 'typescript')}>
                        TypeScript Definitions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportCategory(categoryId, 'csv')}>
                        CSV Field Reference
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportCategory(categoryId, 'openapi')}>
                        OpenAPI Components
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />

      {loading ? (
        <AdminTableSkeleton columns={6} rows={5} />
      ) : standards.length === 0 ? (
        <AdminEmptyState
          icon={Database}
          title="No data standards yet"
          description="Create a new data standard to get started."
          actionLabel="Add Standard"
          onAction={openCreateEditor}
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedStandards).map(([categoryId, categoryStandards]) => (
            <div key={categoryId}>
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                {categoryStandards[0]?.category_name || categoryId} ({categoryStandards.length})
              </h3>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-24">Version</TableHead>
                      <TableHead className="w-20">Status</TableHead>
                      <TableHead className="w-28">Updated</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categoryStandards.map((standard) => (
                      <TableRow 
                        key={standard.id}
                        className="cursor-pointer"
                        onClick={() => openEditEditor(standard)}
                      >
                        <TableCell>
                          <div className="font-medium">{standard.title}</div>
                          <div className="text-xs text-muted-foreground">/{standard.slug}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            v{standard.version}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {standard.is_active ? (
                            <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline">Draft</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(standard.updated_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div 
                            className="flex items-center justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditEditor(standard)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" title="More actions">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => exportStandard(standard)}>
                                  <Download className="h-4 w-4 mr-2" />
                                  Export JSON
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => duplicateStandard(standard)}>
                                  <Copy className="h-4 w-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => confirmDelete(standard)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingStandard ? 'Edit Data Standard' : 'Create Data Standard'}
        description="Define the schema, fields, and example data for this standard."
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={editingStandard ? 'Update' : 'Create'}
      >
        <Tabs value={activeEditorTab} onValueChange={setActiveEditorTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="fields">Fields</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
            <TabsTrigger value="publish">Publish</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Customer"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="e.g., customer"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this data standard..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0.0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              {showCustomCategory ? (
                <div className="flex gap-2">
                  <Input
                    value={customCategoryInput}
                    onChange={(e) => {
                      setCustomCategoryInput(e.target.value);
                      setFormData({
                        ...formData,
                        category: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                        category_name: e.target.value,
                      });
                    }}
                    placeholder="Custom category name"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCustomCategory(false);
                      setCustomCategoryInput('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Select
                    value={formData.category}
                    onValueChange={(value) => {
                      const cat = categories.find(c => c.value === value);
                      setFormData({
                        ...formData,
                        category: value,
                        category_name: cat?.label || value,
                      });
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => setShowCustomCategory(true)}>
                    Custom
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active (visible to users)</Label>
            </div>
          </TabsContent>

          <TabsContent value="fields" className="mt-4">
            <FieldBuilder
              fields={fieldMappings}
              onChange={setFieldMappings}
              lookupGroups={lookupGroups}
            />
          </TabsContent>

          <TabsContent value="preview" className="space-y-6 mt-4">
            <SchemaPreview fields={fieldMappings} />
            <ExampleGenerator
              fields={fieldMappings}
              customExample={customExample}
              onCustomExampleChange={setCustomExample}
            />
          </TabsContent>

          <TabsContent value="advanced" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="custom-schema">Custom Schema Override (JSON)</Label>
              <p className="text-xs text-muted-foreground">
                Leave empty to use auto-generated schema from fields. Only use for advanced customization.
              </p>
              <Textarea
                id="custom-schema"
                value={customSchema}
                onChange={(e) => setCustomSchema(e.target.value)}
                className="font-mono text-sm min-h-[200px]"
                placeholder='{"type": "object", "properties": {...}}'
              />
            </div>
          </TabsContent>

          <TabsContent value="publish" className="space-y-6 mt-4">
            {/* Publish Controls */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Current Version: v{formData.version}</p>
                <p className="text-sm text-muted-foreground">
                  {editingStandard ? 'Publish a new version to create an immutable snapshot.' : 'Save the standard first to enable publishing.'}
                </p>
              </div>
              <PublishVersionDialog
                currentVersion={formData.version}
                standardId={editingStandard?.id || null}
                onPublish={handlePublishVersion}
                disabled={!editingStandard}
              />
            </div>

            {/* Maturity Level */}
            <div className="space-y-2">
              <Label>Maturity Level</Label>
              <Select
                value={formData.maturity_level}
                onValueChange={(value: 'draft' | 'trial' | 'normative' | 'deprecated') => 
                  setFormData({ ...formData, maturity_level: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="normative">Normative</SelectItem>
                  <SelectItem value="deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Deprecation Notice */}
            {formData.maturity_level === 'deprecated' && (
              <div className="space-y-2">
                <Label htmlFor="deprecation-notice" className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  Deprecation Notice
                </Label>
                <Textarea
                  id="deprecation-notice"
                  value={formData.deprecation_notice}
                  onChange={(e) => setFormData({ ...formData, deprecation_notice: e.target.value })}
                  placeholder="Explain why this is deprecated and what users should use instead..."
                  rows={3}
                />
              </div>
            )}

            {/* Version History */}
            {editingStandard && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Version History</h3>
                  <VersionCompare 
                    standardId={editingStandard.id} 
                    currentVersion={formData.version} 
                  />
                </div>
                <VersionHistory
                  standardId={editingStandard.id}
                  currentVersion={formData.version}
                  onRollback={handleRollback}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={standardToDelete?.title}
        itemType="data standard"
        isDeleting={isDeleting}
      />
    </div>
  );
}
